"use client"

import { useState, type CSSProperties } from "react"

import { useParams, usePathname } from "next/navigation"
import { useTranslations } from "next-intl"

import { NavDrawer } from "@/components/marketplace/layout/nav-drawer"
import { SearchOverlay } from "@/components/marketplace/layout/search-overlay"
import { useBagCount } from "@/components/marketplace/layout/use-bag-count"
import { Wordmark } from "@/components/marketplace/layout/wordmark"
import type { FacetOption } from "@/components/marketplace/listing/types"
import type { NavCategory, Product } from "@/components/marketplace/types"
import { BagIcon, CountBadge, IconButton, MenuIcon, Row, SearchIcon } from "@/components/ui"
import { LocalizedClientLink } from "@/components/ui/localized-client-link"
import { DEFAULT_REGION } from "@/lib/region"
import { localizeHref, ROUTES } from "@/lib/routes"

const TABLET_LINKS = [{ href: `/${ROUTES.CATEGORIES}`, label: "categories" }] as const

interface AppHeaderProps {
  categories: NavCategory[]
  bagCount?: number
  suggested?: Product[]
  searchCategoryOptions?: FacetOption[]
}

export const AppHeader = ({ categories, bagCount: bagCountOverride, suggested, searchCategoryOptions }: AppHeaderProps) => {
  const bagCount = useBagCount(bagCountOverride)
  const t = useTranslations("nav")
  const params = useParams<{ countryCode: string }>()
  const pathname = usePathname()
  const countryCode = params.countryCode ?? DEFAULT_REGION
  const [searchOpen, setSearchOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [searchMounted, setSearchMounted] = useState(false)
  const [drawerMounted, setDrawerMounted] = useState(false)
  const openSearch = () => {
    setSearchMounted(true)
    setSearchOpen(true)
  }
  const openDrawer = () => {
    setDrawerMounted(true)
    setDrawerOpen(true)
  }

  return (
    <>
      <div className="bg-surface border-line-subtle @container border-b">
        <Row justify="between" className="relative h-18 px-250 @2xl:px-400">
          <IconButton aria-label={t("openMenu")} variant="ghost" className="text-fg-strong @2xl:hidden" icon={<MenuIcon />} onClick={openDrawer} />
          <div className="contents @2xl:flex @2xl:items-baseline">
            <span className="absolute left-1/2 -translate-x-1/2 @2xl:static @2xl:left-auto @2xl:translate-x-0">
              <LocalizedClientLink
                href="/"
                aria-label={t("brandHome")}
                style={{ transform: "translateY(var(--midline-shift, 0))" } as CSSProperties}
                className="press focus-visible:ring-brand midline-shift-tablet hit-target inline-flex rounded-full focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
              >
                <Wordmark tone="brand" height={22} />
              </LocalizedClientLink>
            </span>
            <Row gap="400" className="text-fg text-body-lg mr-auto ml-500 hidden @2xl:flex">
              {TABLET_LINKS.map(({ href, label }) => {
                const active = href === `/${ROUTES.HOME}` ? pathname === localizeHref("/", countryCode) : pathname.startsWith(localizeHref(href, countryCode))
                return (
                  <LocalizedClientLink
                    key={href}
                    href={href}
                    className={`hit-target inline-flex min-h-11 items-center ${active ? "text-fg-strong font-semibold" : "hover:text-fg-strong"}`}
                  >
                    {t(label)}
                  </LocalizedClientLink>
                )
              })}
            </Row>
          </div>
          <Row gap="50">
            <IconButton aria-label={t("openSearch")} variant="ghost" className="text-fg-strong @2xl:border-line @2xl:border" icon={<SearchIcon size={20} />} onClick={openSearch} />
            <span className="relative">
              <IconButton aria-label={t("bag")} variant="ghost" className="text-fg-strong" asChild>
                <LocalizedClientLink href={`/${ROUTES.CART}`}>
                  <BagIcon />
                </LocalizedClientLink>
              </IconButton>
              {bagCount > 0 && <CountBadge count={bagCount} className="border-surface absolute top-0.5 right-0.5 border-2" />}
            </span>
          </Row>
        </Row>
      </div>

      {searchMounted && (
        <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} suggested={suggested} categories={categories} categoryOptions={searchCategoryOptions} />
      )}
      {drawerMounted && <NavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} categories={categories} bagCount={bagCount} />}
    </>
  )
}
